// public/src/pages/News.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiCalendar, FiUser, FiExternalLink, FiSearch } from 'react-icons/fi';
import axios from 'axios';

function News() {
  const [news, setNews] = useState([]);
  const [filteredNews, setFilteredNews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  useEffect(() => {
    filterNews();
  }, [news, searchTerm]);

  const fetchNews = async () => {
    try {
      const response = await axios.get('https://agri-back-mauve.vercel.app/api/content/public/news');
      if (response.data.success) {
        setNews(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterNews = () => {
    let filtered = news;

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredNews(filtered);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading news...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="news-page">
      {/* Hero Section */}
      <section className="page-hero bg-gradient-to-br from-blue-50 to-cyan-100">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="hero-content text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Afrikonsults & Updates
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Latest developments, announcements, and industry news in African agricultural technology
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search */}
      <section className="search-section bg-white border-b py-8">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search news..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="news-grid-section py-12">
        <div className="container">
          {filteredNews.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📰</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No news found</h3>
              <p className="text-gray-600">
                {searchTerm ? 'Try adjusting your search' : 'No news available yet'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {filteredNews.map((item, index) => (
                <motion.article
                  key={item._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  {/* Featured Image */}
                  {item.image && (
                    <div className="h-64 overflow-hidden">
                      <img
                        src={item.image.startsWith('http') ? item.image : `https://agri-back-mauve.vercel.app${item.image}`}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="p-6">
                    {/* Meta Information */}
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center gap-1">
                        <FiCalendar size={14} />
                        <span>{formatDate(item.publishedAt)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FiUser size={14} />
                        <span>{item.author || 'Afrikonsults'}</span>
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">
                      {item.title}
                    </h2>
                    
                    {/* Subtitle */}
                    {item.subtitle && (
                      <p className="text-gray-600 text-lg mb-4">
                        {item.subtitle}
                      </p>
                    )}
                    
                    {/* Description */}
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {item.description || item.content?.substring(0, 200)}...
                    </p>
                    
                    {/* Read More */}
                    <div className="flex items-center justify-between">
                      <Link
                        to={`/news/${item.seo?.slug || item._id}`}
                        className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors"
                      >
                        Read Full Story <FiExternalLink className="ml-1" />
                      </Link>
                      
                      {item.featured && (
                        <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded">
                          Featured
                        </span>
                      )}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Press Kit CTA */}
      <section className="press-kit-cta bg-gray-900 py-12">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            className="text-center text-white"
          >
            <h2 className="text-3xl font-bold mb-4">Media & Press</h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              For media inquiries, press releases, or interview requests, please contact our media team
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                Contact Media Team
              </a>
              <a
                href="/press-kit"
                className="border border-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
              >
                Download Press Kit
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default News;